import {
    Close as CloseIcon,
    ExpandLess,
    ExpandMore
} from '@mui/icons-material';
import {
    Checkbox,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Grid
} from '@mui/material';
import MDBox from 'components/MDBox';
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from 'context';
import React, { useEffect, useState } from 'react';

const MDFunctionalityModal = ({
    open,
    onClose,
    menusList,
    selectedFunctionalities = [],
    onSave
}) => {
    const [localSelected, setLocalSelected] = useState(selectedFunctionalities);
    const [expandedCategories, setExpandedCategories] = useState({});

    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    const organizeMenusByCategory = (menus) => {
        const categories = {};

        menus.forEach(menu => {
            const categoryName = menu.menu || 'Outros';
            if (!categories[categoryName]) {
                categories[categoryName] = [];
            }
            categories[categoryName].push(menu);
        });

        return categories;
    };

    const categorizedMenus = organizeMenusByCategory(menusList);

    useEffect(() => {
        setLocalSelected(selectedFunctionalities);
        const initialExpanded = {};
        Object.keys(categorizedMenus).forEach(category => {
            initialExpanded[category] = true;
        });
        setExpandedCategories(initialExpanded);
    }, [selectedFunctionalities, open]);

    const handleToggleCategory = (categoryName) => {
        setExpandedCategories(prev => ({
            ...prev,
            [categoryName]: !prev[categoryName]
        }));
    };

    const handleToggleItem = (code, isBaseMenu = false) => {
        setLocalSelected(prev => {
            if (prev.includes(code)) {
                let next = prev.filter(item => item !== code);
                if (isBaseMenu) {
                    next = next.filter(item => !item.startsWith(`${code}_`));
                }
                return next;
            } else {
                if (isBaseMenu) {
                    const toAdd = [code, `${code}_C`, `${code}_U`, `${code}_D`];
                    if (code === 'c050') toAdd.push(`${code}_V`);
                    
                    const newSelected = [...prev];
                    toAdd.forEach(c => {
                        if (!newSelected.includes(c)) newSelected.push(c);
                    });
                    return newSelected;
                }
                return [...prev, code];
            }
        });
    };

    const handleSelectAll = () => {
        const allCodes = menusList.reduce((acc, menu) => {
            acc.push(menu.code, `${menu.code}_C`, `${menu.code}_U`, `${menu.code}_D`);
            if (menu.code === 'c050') acc.push(`${menu.code}_V`);
            return acc;
        }, []);
        setLocalSelected(allCodes);
    };

    const handleSelectNone = () => {
        setLocalSelected([]);
    };

    const handleCategorySelectAll = (categoryMenus) => {
        const categoryCodes = categoryMenus.reduce((acc, menu) => {
            acc.push(menu.code, `${menu.code}_C`, `${menu.code}_U`, `${menu.code}_D`);
            if (menu.code === 'c050') acc.push(`${menu.code}_V`);
            return acc;
        }, []);
        const newSelected = [...localSelected];

        categoryCodes.forEach(code => {
            if (!newSelected.includes(code)) {
                newSelected.push(code);
            }
        });

        setLocalSelected(newSelected);
    };

    const handleCategorySelectNone = (categoryMenus) => {
        const categoryCodes = categoryMenus.reduce((acc, menu) => {
            acc.push(menu.code, `${menu.code}_C`, `${menu.code}_U`, `${menu.code}_D`);
            if (menu.code === 'c050') acc.push(`${menu.code}_V`);
            return acc;
        }, []);
        setLocalSelected(prev => prev.filter(code => !categoryCodes.includes(code)));
    };

    const isCategoryFullySelected = (categoryMenus) => {
        const expectedCodes = categoryMenus.reduce((acc, menu) => {
            acc.push(menu.code, `${menu.code}_C`, `${menu.code}_U`, `${menu.code}_D`);
            if (menu.code === 'c050') acc.push(`${menu.code}_V`);
            return acc;
        }, []);
        return expectedCodes.every(code => localSelected.includes(code));
    };

    const isCategoryPartiallySelected = (categoryMenus) => {
        const expectedCodes = categoryMenus.reduce((acc, menu) => {
            acc.push(menu.code, `${menu.code}_C`, `${menu.code}_U`, `${menu.code}_D`);
            if (menu.code === 'c050') acc.push(`${menu.code}_V`);
            return acc;
        }, []);
        return expectedCodes.some(code => localSelected.includes(code)) && !isCategoryFullySelected(categoryMenus);
    };

    const handleSave = () => {
        onSave(localSelected);
        onClose();
    };

    const handleCancel = () => {
        setLocalSelected(selectedFunctionalities);
        onClose();
    };

    const isAllSelected = menusList.length > 0 && menusList.every(menu => {
        const base = localSelected.includes(menu.code) &&
            localSelected.includes(`${menu.code}_C`) &&
            localSelected.includes(`${menu.code}_U`) &&
            localSelected.includes(`${menu.code}_D`);
        if (menu.code === 'c050') return base && localSelected.includes(`${menu.code}_V`);
        return base;
    });

    const isPartiallySelected = localSelected.length > 0 && !isAllSelected;

    const handleToggleAll = () => {
        if (isAllSelected) {
            handleSelectNone();
        } else {
            handleSelectAll();
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleCancel}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    minHeight: '400px',
                    maxHeight: '85vh',
                    backgroundColor: darkMode ? '#4B4F5C' : '#fff',
                    color: darkMode ? '#fff' : '#000',
                    padding: '8px'
                }
            }}
        >
            <DialogTitle sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                pb: 1,
                backgroundColor: darkMode ? '#4B4F5C' : '#f5f5f5',
                color: darkMode ? '#fff' : '#000',
            }}>
                <MDTypography variant="h6" fontWeight="bold">
                    Configurar Acessos
                </MDTypography>
                <IconButton onClick={handleCancel} size="small" sx={{ color: darkMode ? '#fff' : '#000' }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 0 }}>
                <MDBox sx={{ 
                    px: 2, 
                    py: 1, 
                    borderBottom: '1px solid #e0e0e0', 
                    backgroundColor: darkMode ? '#4B4F5C' : '#fafafa',
                    display: 'flex',
                    alignItems: 'center'
                }}>
                    <Checkbox
                        checked={isAllSelected}
                        indeterminate={isPartiallySelected}
                        onChange={handleToggleAll}
                        color="info"
                        sx={{ p: 0.5, mr: 1 }}
                    />
                    <MDTypography variant="button" fontWeight="medium">
                        Selecionar Todos os Acessos
                    </MDTypography>
                    <MDBox sx={{ flexGrow: 1 }} />
                    <MDTypography variant="caption" sx={{ fontSize: '10px', opacity: 0.8 }}>
                        {localSelected.filter(code => !code.includes('_')).length} de {menusList.length} selecionados
                    </MDTypography>
                </MDBox>

                <List sx={{ py: 0 }}>
                    {Object.entries(categorizedMenus).map(([categoryName, categoryMenus]) => (
                        <React.Fragment key={categoryName}>
                            <ListItem
                                button
                                onClick={() => handleToggleCategory(categoryName)}
                                sx={{
                                    backgroundColor: darkMode ? '#4B4F5C' : '#f0f2f5',
                                    borderBottom: darkMode ? '1px solid #545866' : '1px solid #e0e6ed',
                                    color: darkMode ? '#fff' : '#344767',
                                    py: 1,
                                    px: 2,
                                    mt: 0.5,
                                    borderRadius: '4px'
                                }}
                            >
                                <Checkbox
                                    size="small"
                                    checked={isCategoryFullySelected(categoryMenus)}
                                    indeterminate={isCategoryPartiallySelected(categoryMenus)}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => {
                                        e.stopPropagation();
                                        if (isCategoryFullySelected(categoryMenus)) {
                                            handleCategorySelectNone(categoryMenus);
                                        } else {
                                            handleCategorySelectAll(categoryMenus);
                                        }
                                    }}
                                    sx={{ p: 0.5, mr: 1 }}
                                />
                                <ListItemText
                                    primary={
                                        <MDTypography variant="button" fontWeight="bold" sx={{ fontSize: '13px' }}>
                                            {categoryName}
                                        </MDTypography>
                                    }
                                />
                                {expandedCategories[categoryName] ? <ExpandLess /> : <ExpandMore />}
                            </ListItem>

                            <Collapse in={expandedCategories[categoryName]} timeout="auto" unmountOnExit>
                                <List component="div" disablePadding>
                                    {categoryMenus.map((menu) => (
                                        <ListItem
                                            key={menu.code}
                                            sx={{ py: 0.5, pl: 4, pr: 2, borderBottom: darkMode ? '1px dashed #333' : '1px dashed #eee' }}
                                        >
                                            <Grid container alignItems="center">
                                                <Grid item xs={12} sm={5} display="flex" alignItems="center">
                                                    <Checkbox
                                                        size="small"
                                                        checked={localSelected.includes(menu.code)}
                                                        onChange={() => handleToggleItem(menu.code, true)}
                                                        sx={{ p: 0.5, mr: 1 }}
                                                    />
                                                    <MDTypography variant="button" fontWeight="medium" sx={{ fontSize: '12px' }}>
                                                        {menu.name}
                                                    </MDTypography>
                                                </Grid>
                                                <Grid item xs={12} sm={7}>
                                                    <Grid container>
                                                        {menu.code === 'c050' && (
                                                            <Grid item xs={3} display="flex" alignItems="center">
                                                                <Checkbox
                                                                    size="small"
                                                                    checked={localSelected.includes(`${menu.code}_V`)}
                                                                    onChange={() => handleToggleItem(`${menu.code}_V`)}
                                                                    disabled={!localSelected.includes(menu.code)}
                                                                    sx={{ p: 0.5 }}
                                                                />
                                                                <MDTypography variant="caption" sx={{ fontSize: '11px' }}>Visualizar</MDTypography>
                                                            </Grid>
                                                        )}
                                                        <Grid item xs={menu.code === 'c050' ? 3 : 4} display="flex" alignItems="center">
                                                            <Checkbox
                                                                size="small"
                                                                checked={localSelected.includes(`${menu.code}_C`)}
                                                                onChange={() => handleToggleItem(`${menu.code}_C`)}
                                                                disabled={!localSelected.includes(menu.code)}
                                                                sx={{ p: 0.5 }}
                                                            />
                                                            <MDTypography variant="caption" sx={{ fontSize: '11px' }}>Criar</MDTypography>
                                                        </Grid>
                                                        <Grid item xs={menu.code === 'c050' ? 3 : 4} display="flex" alignItems="center">
                                                            <Checkbox
                                                                size="small"
                                                                checked={localSelected.includes(`${menu.code}_U`)}
                                                                onChange={() => handleToggleItem(`${menu.code}_U`)}
                                                                disabled={!localSelected.includes(menu.code)}
                                                                sx={{ p: 0.5 }}
                                                            />
                                                            <MDTypography variant="caption" sx={{ fontSize: '11px' }}>Editar</MDTypography>
                                                        </Grid>
                                                        <Grid item xs={menu.code === 'c050' ? 3 : 4} display="flex" alignItems="center">
                                                            <Checkbox
                                                                size="small"
                                                                checked={localSelected.includes(`${menu.code}_D`)}
                                                                onChange={() => handleToggleItem(`${menu.code}_D`)}
                                                                disabled={!localSelected.includes(menu.code)}
                                                                sx={{ p: 0.5 }}
                                                            />
                                                            <MDTypography variant="caption" sx={{ fontSize: '11px' }}>Excluir</MDTypography>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </ListItem>
                                    ))}
                                </List>
                            </Collapse>
                        </React.Fragment>
                    ))}
                </List>
            </DialogContent>

            <Divider />

            <DialogActions sx={{ p: 2, justifyContent: 'flex-end' }}>
                <MDButton onClick={handleCancel} color="light">
                    Cancelar
                </MDButton>
                <MDButton
                    onClick={handleSave}
                    variant="contained"
                    color="info"
                >
                    Confirmar Seleção
                </MDButton>
            </DialogActions>
        </Dialog>
    );
};

export default MDFunctionalityModal;
